import {ReactElement, useEffect, useState} from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  SelectChangeEvent,
  Snackbar,
  Tooltip,
  Typography
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import LoadingIndicator from 'components/shared/ui/LoadingIndicator';
import {PROJECT, PROJECTS, PROJECT_PDF} from 'const/backend';
import useConfirmDialogContext from 'contexts/ConfirmDialogContext/useConfirmDialogContext';
import useApiMutation from 'hooks/useApiMutation';
import {ApiMutationStatus, ApiQueryStatus} from 'interfaces/ApiRequestStatus';
import {Project} from 'interfaces/Project';
import {System} from 'interfaces/System';
import generatePathWithQueryParams from 'utils/generatePathWithQueryParams';
import ProjectConfigurator from './components/ProjectConfigurator';
import SelectProject from './components/SelectProject';
import {UseConfiguratorActions} from './hooks/useConfigurator';
import {UseSaveProject} from './hooks/useConfigurator/useSaveProject';

type Props = {
  selectProjectForm: {
    value: string | null;
    options: Project[];
  };
  onSelectProject: (event: SelectChangeEvent) => void;
  selectFirstProject: () => void;
  editableProject: Project | null;
  systems: System[];
  configuratorActions: UseConfiguratorActions;
  getProjectStatus: ApiQueryStatus;
  saveProject: UseSaveProject;
  onCreate: () => void;
  createProjectRequestStatus: ApiMutationStatus;
};

export default function CalculatorView({
  selectProjectForm,
  onSelectProject,
  selectFirstProject,
  editableProject,
  systems,
  configuratorActions,
  getProjectStatus,
  saveProject,
  onCreate,
  createProjectRequestStatus
}: Props): ReactElement {
  const {t} = useTranslation('common');
  const [isErrorSnackbarVisible, setIsErrorSnackbarVisible] = useState(false);
  const {mutate: cloneProject, apiMutationStatus: cloneProjectRequestStatus} =
    useApiMutation<Project>();
  const {mutate: deleteProject, apiMutationStatus: deleteProjectRequestStatus} =
    useApiMutation<Project>();
  const {mutate: requestPdf, apiMutationStatus: requestPdfStatus} = useApiMutation<Blob>();
  const openConfirmDialog = useConfirmDialogContext();

  function handleCloseErrorSnackbar() {
    setIsErrorSnackbarVisible(false);
  }

  useEffect(
    function () {
      if (saveProject.requestStatus.isError) {
        setIsErrorSnackbarVisible(true);
      }
    },
    [saveProject.requestStatus.isError]
  );

  function handleSave() {
    if (editableProject) {
      saveProject.send(editableProject);
    }
  }

  function handleClone() {
    if (!editableProject) {
      return;
    }
    openConfirmDialog({
      onConfirm: () =>
        saveProject.send(editableProject, () =>
          cloneProject(
            {
              path: PROJECTS,
              method: 'POST',
              body: {
                sourceId: editableProject.id
              }
            },
            {
              onSuccess: function () {
                selectFirstProject();
              }
            }
          )
        ),
      title: t('common:unsavedChanges.title'),
      text: t('common:unsavedChanges.text')
    });
  }

  function handleDelete() {
    if (!editableProject) {
      return;
    }
    openConfirmDialog({
      onConfirm: () =>
        deleteProject(
          {
            path: generatePathWithQueryParams(PROJECT, {id: `${editableProject.id}`}),
            method: 'DELETE'
          },
          {
            onSuccess: function () {
              selectFirstProject();
            }
          }
        ),
      title: t('common:deleteProject.dialog.title'),
      text: t('common:deleteProject.dialog.text')
    });
  }

  function handleGeneratePdf() {
    if (!editableProject) {
      return;
    }
    saveProject.send(editableProject, function () {
      requestPdf(
        {
          method: 'GET',
          path: generatePathWithQueryParams(PROJECT_PDF, {id: `${editableProject.id}`}),
          responseIsBlob: true
        },
        {
          onSuccess: function (response) {
            const fileUrl = URL.createObjectURL(response.data);
            window.open(fileUrl);
          }
        }
      );
    });
  }

  const someActionRequestIsSending =
    requestPdfStatus.isLoading || saveProject.requestStatus.isLoading || deleteProjectRequestStatus.isLoading;

  return (
    <Box>
      <Grid container spacing={4}>
        <Grid item xs={12} lg={9} sx={{order: {xs: 2, lg: 2}}}>
          {editableProject && !getProjectStatus.fetchStatus.isFetching && (
            <Paper elevation={3} sx={{mb: 4, overflow: 'hidden'}}>
              <ProjectConfigurator
                editableProject={editableProject}
                systems={systems}
                saveProject={saveProject}
                configuratorActions={configuratorActions}
              />
            </Paper>
          )}
          {getProjectStatus.dataStatus.isLoading && (
            <LoadingIndicator centered text={t('common:loadingProject')} />
          )}
          {createProjectRequestStatus.isLoading && (
            <LoadingIndicator text={t('common:creatingProject')} />
          )}
        </Grid>
        <Grid
          item
          xs={12}
          lg={3}
          sx={{
            order: {xs: 1, lg: 1},
            position: {lg: 'sticky'},
            top: {lg: 16},
            alignSelf: {lg: 'flex-start'}
          }}
        >
          <Paper elevation={3} sx={{p: 2, overflow: 'hidden'}}>
            <Typography variant="h6" sx={{mb: 2}}>
              {t('common:myProjects')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <SelectProject
                  t={t}
                  onSelectProject={onSelectProject}
                  selectProjectForm={selectProjectForm}
                />
              </Grid>
              <Grid item xs={12}>
                <Button onClick={onCreate} variant="outlined" fullWidth color="secondary">
                  {t('common:createProject')}
                </Button>
              </Grid>
            </Grid>
          </Paper>
          {editableProject && !getProjectStatus.fetchStatus.isFetching && (
            <Paper elevation={3} sx={{p: 2, mt: 4, overflow: 'hidden'}}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                <Tooltip title={t('common:cloneCurrentProject')}>
                  <IconButton
                    onClick={handleClone}
                    color="secondary"
                    size="small"
                    disabled={someActionRequestIsSending}
                    sx={{width: 34, height: 34}}
                  >
                    <FileCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('common:generatePdf')}>
                  <IconButton
                    onClick={handleGeneratePdf}
                    color="secondary"
                    size="small"
                    disabled={someActionRequestIsSending}
                    sx={{width: 34, height: 34}}
                  >
                    <PictureAsPdfIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('common:delete')}>
                  <IconButton
                    onClick={handleDelete}
                    color="error"
                    size="small"
                    disabled={someActionRequestIsSending}
                    sx={{width: 34, height: 34}}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Button
                  onClick={handleSave}
                  variant="contained"
                  disabled={someActionRequestIsSending}
                  sx={{flexGrow: 1}}
                >
                  {saveProject.requestStatus.isLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    t('common:saveOnly')
                  )}
                </Button>
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>
      <Snackbar
        open={isErrorSnackbarVisible}
        autoHideDuration={6000}
        onClose={handleCloseErrorSnackbar}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
      >
        <Alert severity="error" sx={{width: '100%'}}>
          {t('common:saveProjectError')}
        </Alert>
      </Snackbar>
      <Snackbar
        open={cloneProjectRequestStatus.isLoading}
        autoHideDuration={6000}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
      >
        <Alert severity="info" sx={{width: '100%'}}>
          {t('common:creatingProject')}
        </Alert>
      </Snackbar>
      <Snackbar
        open={requestPdfStatus.isError}
        autoHideDuration={6000}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
      >
        <Alert severity="error" sx={{width: '100%'}}>
          {t('common:generatePdfError')}
        </Alert>
      </Snackbar>
      <Snackbar
        open={deleteProjectRequestStatus.isError}
        autoHideDuration={6000}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
      >
        <Alert severity="error" sx={{width: '100%'}}>
          {t('common:deleteProject.error')}
        </Alert>
      </Snackbar>
    </Box>
  );
}
